import React, { useEffect, useState } from 'react';
import { PAGE } from './SettingsUI';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Phone, Mail, UserCheck, Clock, Users, HeartHandshake,
  ShieldCheck, Loader2, Globe, Edit2, CalendarDays,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import BranchAccess from './BranchAccess';
import { useBranch } from '../../context/BranchContext';

/**
 * One branch, opened from the Locations list.
 *
 * The list previously tracked a `selected` branch and rendered nothing from it, so
 * clicking a card highlighted it and did nothing else. This is what clicking should have
 * led to: who works here, who is supported here, and who is allowed in.
 */
export default function BranchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { branchId: activeBranch, setBranchId } = useBranch();

  const [branch, setBranch] = useState(null);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [access, setAccess] = useState([]);
  const [tab, setTab] = useState('staff');
  const [loading, setLoading] = useState(true);
  const [showAccess, setShowAccess] = useState(false);

  const load = async () => {
    // Nothing to load without an id, and asking anyway produced /locations/undefined —
    // three failing requests and an error toast for what is really just a bad link.
    if (!id) {
      toast.error('That branch link is missing an id');
      navigate('/settings/locations');
      return;
    }
    try {
      const [b, s, c] = await Promise.all([
        api.get(`/locations/${id}`),
        api.get(`/locations/${id}/staff`).catch(() => ({ data: [] })),
        api.get(`/locations/${id}/clients`).catch(() => ({ data: [] })),
      ]);
      setBranch(b.data);
      setStaff(s.data || []);
      setClients(c.data || []);
      // Access is a separate question from "based here", so it is loaded separately.
      api.get(`/locations/${id}/access`)
        .then(r => setAccess((r.data || []).filter(p => p.has_access)))
        .catch(() => {});
    } catch (e) {
      // 404 means it is gone or not theirs; anything else is a genuine failure and should
      // not be reported as if the branch simply did not exist.
      toast.error(e.response?.status === 404
        ? 'That branch no longer exists, or you do not have access to it'
        : 'Could not load this branch');
      navigate('/settings/locations');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading branch…
      </div>
    );
  }
  if (!branch) return null;

  const TABS = [
    { key: 'staff',   label: 'Based here',   count: staff.length,   icon: Users },
    { key: 'access',  label: 'Can work here', count: access.length, icon: ShieldCheck },
    { key: 'clients', label: 'Participants', count: clients.length, icon: HeartHandshake },
  ];

  return (
    <div className={PAGE}>
      <Link to="/settings/locations"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft size={15} /> All branches
      </Link>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-2xl bg-indigo-100 grid place-items-center shrink-0">
              <MapPin size={22} className="text-indigo-600" />
            </span>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{branch.name}</h1>
                {branch.code && (
                  <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-500">
                    {branch.code}
                  </span>
                )}
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  branch.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {branch.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="mt-2 space-y-1 text-sm text-slate-500">
                {branch.address && <p className="flex items-center gap-2"><MapPin size={13} /> {branch.address}</p>}
                {branch.phone   && <p className="flex items-center gap-2"><Phone size={13} /> {branch.phone}</p>}
                {branch.email   && <p className="flex items-center gap-2"><Mail size={13} /> {branch.email}</p>}
                {branch.manager && <p className="flex items-center gap-2"><UserCheck size={13} /> Manager: {branch.manager}</p>}
                {branch.timezone && <p className="flex items-center gap-2"><Globe size={13} /> {branch.timezone}</p>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Working in a branch means the whole product pointed at it, not one screen
                about it — so this switches the app rather than opening a sub-page. */}
            {activeBranch === branch.id ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-semibold">
                <CalendarDays size={15} /> You are working in this branch
              </span>
            ) : (
              <button onClick={() => setBranchId(branch.id)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
                <CalendarDays size={15} /> Work in this branch
              </button>
            )}
            <button onClick={() => setShowAccess(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">
              <ShieldCheck size={15} /> Manage access
            </button>
            <Link to="/settings/locations"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              <Edit2 size={14} /> Edit
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-slate-100">
          <Stat icon={Users} label="Staff based here" value={staff.length} />
          <Stat icon={ShieldCheck} label="Can work here" value={access.length} />
          <Stat icon={HeartHandshake} label="Participants" value={clients.length} />
          <Stat icon={Clock} label="Shifts (30d)" value={branch.shifts_last_30 ?? 0} />
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-1.5 mb-4 inline-flex gap-1">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === t.key ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
            <t.icon size={14} /> {t.label}
            <span className={`text-[11px] font-bold ${tab === t.key ? 'text-indigo-200' : 'text-slate-400'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {tab === 'staff' && (
        <Panel
          empty="Nobody is based at this branch yet. Set someone's branch on their staff profile."
          rows={staff}
          render={p => (
            <>
              <span className="font-semibold text-slate-800">{p.first_name} {p.last_name}</span>
              <span className="text-xs text-slate-400">{p.email} · {p.role}</span>
            </>
          )}
        />
      )}

      {tab === 'access' && (
        <>
          <p className="text-xs text-slate-500 mb-2 leading-relaxed">
            Staff explicitly allowed to work here. Anyone not assigned to <em>any</em> branch can
            still work at all of them — access narrows only for people you assign.
          </p>
          <Panel
            empty="No explicit access list. Everyone can work at this branch."
            rows={access}
            render={p => (
              <>
                <span className="font-semibold text-slate-800">
                  {p.first_name} {p.last_name}
                  {p.is_home_branch && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-indigo-600">based here</span>
                  )}
                </span>
                <span className="text-xs text-slate-400">{p.email} · {p.role}</span>
              </>
            )}
          />
        </>
      )}

      {tab === 'clients' && (
        <Panel
          empty="No participants are assigned to this branch yet."
          rows={clients}
          render={c => (
            <>
              <span className="font-semibold text-slate-800">{c.first_name} {c.last_name}</span>
              <span className="text-xs text-slate-400">{c.ndis_number || 'No NDIS number'}</span>
            </>
          )}
        />
      )}

      {showAccess && (
        <BranchAccess branch={branch} onClose={(changed) => { setShowAccess(false); if (changed) load(); }} />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-slate-400 mb-1">
        <Icon size={13} />
        <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black text-slate-900 tabular-nums">{value}</p>
    </div>
  );
}

function Panel({ rows, render, empty }) {
  if (!rows.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
        <p className="text-sm text-slate-400">{empty}</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {rows.map((r, i) => (
        <div key={r.id || i}
          className={`flex flex-col px-4 py-3 ${i ? 'border-t border-slate-100' : ''}`}>
          {render(r)}
        </div>
      ))}
    </div>
  );
}
